import { useState, useEffect } from 'react';
import { X, Loader2, Paperclip, Plus, Trash2 } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import type { Demanda, Solicitante, Area, Pessoa, Prioridade, Origem, StatusDemanda, Evento, CategoriaDemanda, SubItemTipoB, Canal, Contrato, Episodio } from '@/lib/types';
import { CHECKLIST_TEMPLATES, TIPOS_APOSENTADOS } from '@/lib/checklists';
import type { TipoProduto } from '@/lib/checklists';
import { resolveAreasTipoProduto, COORDENADORES_CANAL } from '@/lib/materialAreaMap';

interface Props {
  open: boolean;
  onClose: () => void;
  solicitantes: Solicitante[];
  areas: Area[];
  pessoas?: Pessoa[];
  eventos?: Evento[];
  contratos?: Contrato[];
  episodios?: Episodio[];
}

const PRIORIDADES: Prioridade[] = ['Crítica', 'Alta', 'Média', 'Baixa'];
const ORIGENS: Origem[] = ['Externo (contratual)', 'Apex (UN)', 'Research (interno)'];

/** Agrupa solicitantes por categoria (Cliente externo / UN / Research) e ordena alfabeticamente */
function agruparSolicitantes(solicitantes: Solicitante[]) {
  const grupos: Record<string, Solicitante[]> = {
    'Clientes externos': [],
    'Unidades Apex': [],
    'Time Research': [],
  };
  for (const s of solicitantes) {
    if (s.status !== 'Ativo') continue;
    if (s.tipo === 'Cliente externo') grupos['Clientes externos'].push(s);
    else if (s.tipo === 'Unidade Apex') grupos['Unidades Apex'].push(s);
    else if (s.tipo === 'Time Research') grupos['Time Research'].push(s);
  }
  for (const k of Object.keys(grupos)) {
    grupos[k].sort((a, b) => a.nome.localeCompare(b.nome));
  }
  return grupos;
}

/** Tipos de produto (legado) de conteúdo/vídeo que pedem o Canal (v2). */
const TIPOS_COM_CANAL: TipoProduto[] = [
  'Matéria de TV', 'Cliente Digital', 'Conteúdo editorial',
  'Vídeo institucional', 'Post de rede social', 'Campanha',
];

/** Origem restringe os grupos de solicitantes exibidos. */
const GRUPOS_POR_ORIGEM: Record<Origem, string[]> = {
  'Externo (contratual)': ['Clientes externos'],
  'Apex (UN)': ['Unidades Apex'],
  'Research (interno)': ['Time Research'],
};

/**
 * Times que entregam cada tipo de produto (proposta inicial — validar com coordenadores).
 * v2 (03/07/2026): "Design" deixou de ser área (é Branding); "Dados" consolidada em "Análise".
 * A fonte definitiva do roteamento é materialAreaMap.ts (auto-roteamento full no S2).
 */
const AREAS_POR_TIPO: Partial<Record<TipoProduto, string[]>> = {
  'Matéria de TV': ['Produtora', 'Conteúdo', 'Branding'],
  'Vídeo institucional': ['Produtora', 'Branding', 'Conteúdo'],
  'Foto / Galeria': ['Produtora', 'Branding'],
  'Post de rede social': ['Conteúdo', 'Branding'],
  'Conteúdo editorial': ['Conteúdo'],
  'Cliente Digital': ['Conteúdo', 'Branding', 'MAC'],
  'Evento inter time': ['Eventos', 'Conteúdo', 'Branding', 'Produtora', 'MAC'],
  'Pesquisa': ['Análise'],
  'Campanha': ['Conteúdo', 'Branding', 'Produtora'],
  'Cobertura de evento': ['Conteúdo', 'Produtora'],
  'Vídeo para evento': ['Conteúdo', 'Produtora', 'Eventos'],
  // 'Outro' → todas
};

/**
 * Casa nome de área (Airtable) com um token sugerido, tolerante a nomes ainda
 * não renomeados (ex.: token "Branding" casa com área "Design / Branding";
 * renomear os registros no Airtable é tarefa manual do S0).
 */
function areaCasaTokens(nomeArea: string, tokens?: string[]): boolean {
  if (!tokens) return true;
  const n = nomeArea.toLowerCase();
  return tokens.some((t) => {
    const tk = t.toLowerCase();
    return n === tk || n.includes(tk) || tk.includes(n);
  });
}

export function NovaDemandaModal({ open, onClose, solicitantes, areas, pessoas = [], eventos = [], contratos = [], episodios = [] }: Props) {
  const qc = useQueryClient();
  const [titulo, setTitulo] = useState('');
  const [solicitanteId, setSolicitanteId] = useState('');
  const [contratoId, setContratoId] = useState('');
  // Multi-cota já na criação: 1+ contratos (1 por cliente atendido).
  const [contratosSel, setContratosSel] = useState<string[]>([]);
  const [prioridade, setPrioridade] = useState<Prioridade>('Média');
  const [origem, setOrigem] = useState<Origem>('Externo (contratual)');
  const [prazoSla, setPrazoSla] = useState('');
  const [briefing, setBriefing] = useState('');
  const [anexos, setAnexos] = useState<string[]>([]);
  const [novoAnexo, setNovoAnexo] = useState('');
  const [areasIds, setAreasIds] = useState<string[]>([]);
  const [mostrarTodasAreas, setMostrarTodasAreas] = useState(false);
  const [splitByArea, setSplitByArea] = useState(true);
  const [tipoProduto, setTipoProduto] = useState<TipoProduto>('Outro');
  const [areasApoioIds, setAreasApoioIds] = useState<string[]>([]);
  const [canal, setCanal] = useState<Canal | ''>('');
  const [executorId, setExecutorId] = useState('');
  const [colaboradores, setColaboradores] = useState<string[]>([]);
  const [episodioId, setEpisodioId] = useState('');
  const [categoriaDemanda, setCategoriaDemanda] = useState<CategoriaDemanda>('tipo_a');
  const [eventoId, setEventoId] = useState('');
  const [clientesTipoB, setClientesTipoB] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);

  const grupos = agruparSolicitantes(solicitantes);
  const idsDisponiveis = new Set(areas.map((a) => a.id));

  /**
   * Auto-roteamento v2.1: ao trocar tipo OU canal, sugere áreas dono (pré-marcadas)
   * + apoio. Regra por canal: TV → Produtora dona (Conteúdo vira apoio); digital →
   * Conteúdo dono. 'Matéria de TV' é sempre TV → default canal 'tv'.
   */
  function aplicarRoteamento(tp: TipoProduto, canalArg?: Canal | '') {
    const canalEfetivo: Canal | '' = TIPOS_COM_CANAL.includes(tp)
      ? (canalArg ?? (tp === 'Matéria de TV' ? 'tv' : ''))
      : '';
    const { areasIds: dono, areasApoioIds: apoio } = resolveAreasTipoProduto(
      tp, undefined, canalEfetivo || undefined,
    );
    setAreasIds(dono.filter((id) => idsDisponiveis.has(id)));
    setAreasApoioIds(apoio.filter((id) => idsDisponiveis.has(id)));
    setCanal(canalEfetivo);
  }

  useEffect(() => {
    if (open) {
      setTitulo(''); setSolicitanteId(''); setContratoId(''); setPrioridade('Média');
      setOrigem('Externo (contratual)'); setPrazoSla(''); setBriefing('');
      setAnexos([]); setNovoAnexo(''); setAreasIds([]); setSplitByArea(true);
      setTipoProduto('Outro'); setAreasApoioIds([]); setCanal('');
      setExecutorId(''); setColaboradores([]); setEpisodioId(''); setCategoriaDemanda('tipo_a');
      setEventoId(''); setClientesTipoB([]); setContratosSel([]); setErr(null);
    }
  }, [open]);

  const create = useMutation({
    mutationFn: (body: Partial<Demanda>) => api.demandas.create(body).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['demandas'] }); onClose(); },
    onError: (e) => {
      const msg = e instanceof ApiError ? (e.body as { message?: string })?.message ?? e.message : String(e);
      setErr(msg);
    },
  });

  if (!open) return null;

  function adicionarAnexo() {
    const v = novoAnexo.trim();
    if (!v) return;
    setAnexos((arr) => [...arr, v]);
    setNovoAnexo('');
  }

  function removerAnexo(i: number) {
    setAnexos((arr) => arr.filter((_, idx) => idx !== i));
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!titulo.trim()) { setErr('Título é obrigatório'); return; }
    setErr(null);
    const briefingFull = anexos.length
      ? [briefing.trim(), '\n— Anexos —', ...anexos].filter(Boolean).join('\n')
      : briefing.trim();
    const payload: Partial<Demanda> & { _splitByArea?: boolean } = {
      titulo: titulo.trim(),
      solicitanteId: categoriaDemanda === 'tipo_b' && clientesTipoB.length > 0
        ? clientesTipoB[0]
        : (solicitanteId || undefined),
      prioridade, origem,
      status: 'Triagem' as StatusDemanda,
      prazoSla: prazoSla || undefined,
      briefing: briefingFull || undefined,
      contratosIds: contratosSel.length > 0 ? contratosSel : undefined,
      areasIds: areasIds.length > 0 ? areasIds : undefined,
      areasApoioIds: areasApoioIds.length > 0 ? areasApoioIds : undefined,
      canal: (canal || undefined) as Canal | undefined,
      colaboradoresIds: colaboradores.length > 0 ? colaboradores : undefined,
      episodioId: episodioId || undefined,
      tipoProduto,
      executorId: executorId || undefined,
      categoriaDemanda,
      eventoId: eventoId || undefined,
      faseEvento: tipoProduto === 'Pesquisa' && eventoId ? 'pre' : undefined,
      _splitByArea: areasIds.length > 1 ? splitByArea : undefined,
    };
    if (categoriaDemanda === 'tipo_b' && clientesTipoB.length > 0) {
      const subItems: SubItemTipoB[] = clientesTipoB.map((sid) => {
        const sol = solicitantes.find((s) => s.id === sid);
        return { solicitanteId: sid, solicitanteNome: sol?.nome, status: 'pendente' as const };
      });
      payload.subItemsTipoB = JSON.stringify(subItems);
    }
    create.mutate(payload);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-6" tabIndex={-1} ref={(el)=>{if(el&&el.dataset.esc!=='1'){el.dataset.esc='1';el.focus();}}} onKeyDown={(e)=>{if(e.key==='Escape')onClose();}}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="bg-panel border border-border rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h2 className="text-sm font-bold">Nova demanda</h2>
          <button type="button" onClick={onClose} className="text-muted hover:text-text"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Título" required>
            <input autoFocus className="input w-full text-sm" value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Ex.: Vídeo institucional · Cooperativa Y" />
          </Field>

          {/* ORIGEM PRIMEIRO, depois SOLICITANTE */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Origem">
              <select className="input w-full text-sm" value={origem} onChange={(e) => { setOrigem(e.target.value as Origem); setSolicitanteId(''); setContratoId(''); }}>
                {ORIGENS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Solicitante">
              <select className="input w-full text-sm" value={solicitanteId} onChange={(e) => {
                const sid = e.target.value;
                setSolicitanteId(sid);
              }}>
                <option value="">— escolha um —</option>
                {Object.entries(grupos)
                  .filter(([grupo]) => (GRUPOS_POR_ORIGEM[origem] ?? []).includes(grupo))
                  .map(([grupo, lista]) =>
                    lista.length ? (
                      <optgroup key={grupo} label={grupo}>
                        {lista.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
                      </optgroup>
                    ) : null,
                  )}
              </select>
            </Field>
          </div>

          {/* CONTRATO — herança por contrato (credita a cota) */}
          {origem === 'Externo (contratual)' && (
            <Field label="Contrato do cliente (herança por contrato)">
              <select
                className="input w-full text-sm"
                value={contratoId}
                onChange={(e) => {
                  const id = e.target.value;
                  if (!id) { setContratoId(''); return; }
                  setContratosSel((arr) => (arr.includes(id) ? arr : [...arr, id]));
                  setContratoId('');
                  const ct = contratos.find((c) => c.id === id);
                  if (ct?.solicitanteId && !solicitanteId) setSolicitanteId(ct.solicitanteId);
                }}
              >
                <option value="">{contratosSel.length > 0 ? '+ adicionar outro contrato…' : '— sem contrato (card avulso) —'}</option>
                {contratos
                  .filter((c) => !contratosSel.includes(c.id))
                  .slice()
                  .sort((a, b) => (a.identificador ?? '').localeCompare(b.identificador ?? ''))
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.identificador}{c.solicitanteNome ? ` · ${c.solicitanteNome}` : ''}{c.status && c.status !== 'Vigente' ? ` (${c.status})` : ''}
                    </option>
                  ))}
              </select>
              {contratosSel.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {contratosSel.map((id) => {
                    const ct = contratos.find((c) => c.id === id);
                    return (
                      <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-panel-2 border border-border text-[11px] text-text">
                        {ct?.identificador ?? id}{ct?.solicitanteNome ? ` · ${ct.solicitanteNome}` : ''}
                        <button type="button" className="text-muted hover:text-red-500 font-bold" onClick={() => setContratosSel((arr) => arr.filter((x) => x !== id))}>×</button>
                      </span>
                    );
                  })}
                </div>
              )}
              <p className="text-[10px] text-muted mt-1">
                {contratosSel.length > 0
                  ? 'Esta demanda credita o(s) contrato(s) acima — multi-cota já na criação. Times vêm da matriz pelo Tipo de produto.'
                  : 'Adicione 1 contrato por cliente atendido — a demanda herda os times pela matriz e credita as cotas. Sem contrato = card avulso.'}
              </p>
            </Field>
          )}

          {/* TIPO A / TIPO B */}
          <Field label="Categoria da demanda">
            <div className="flex gap-2">
              <button type="button" onClick={() => setCategoriaDemanda('tipo_a')}
                className={`flex-1 text-xs py-2 rounded border font-semibold transition-colors ${categoriaDemanda === 'tipo_a' ? 'bg-blue/15 border-blue/40 text-blue-light' : 'bg-panel-2 border-border text-text-2 hover:border-border-light'}`}>
                Tipo A · Individual
              </button>
              <button type="button" onClick={() => setCategoriaDemanda('tipo_b')}
                className={`flex-1 text-xs py-2 rounded border font-semibold transition-colors ${categoriaDemanda === 'tipo_b' ? 'bg-purple/15 border-purple/40 text-purple' : 'bg-panel-2 border-border text-text-2 hover:border-border-light'}`}>
                Tipo B · Evento (N clientes)
              </button>
            </div>
            <p className="text-[10px] text-muted mt-1">
              {categoriaDemanda === 'tipo_a'
                ? 'Card individual por cliente. Fluxo normal de produção.'
                : 'Card de evento com vários clientes dentro. Ex: "Logo no palco — BUY ES".'}
            </p>
          </Field>

          {categoriaDemanda === 'tipo_b' && (
            <>
              <Field label="Evento vinculado">
                <select className="input w-full text-sm" value={eventoId} onChange={(e) => {
                  setEventoId(e.target.value);
                  // Pré-carrega clientes do evento
                  const ev = eventos.find((evt) => evt.id === e.target.value);
                  if (ev?.solicitantesIds) setClientesTipoB(ev.solicitantesIds);
                }}>
                  <option value="">— escolha o evento —</option>
                  {eventos
                    .filter((ev) => ev.status === 'Confirmado' || ev.status === 'Planejado')
                    .map((ev) => <option key={ev.id} value={ev.id}>{ev.nome} ({ev.status})</option>)}
                </select>
              </Field>
              {tipoProduto === 'Vídeo para evento' && eventoId && (() => {
                const evSel = eventos.find((evt) => evt.id === eventoId);
                if (!evSel?.dataInicio) return null;
                const diasAte = Math.ceil((new Date(`${evSel.dataInicio.slice(0, 10)}T12:00:00`).getTime() - Date.now()) / 86400000);
                if (diasAte >= 10) return null;
                return (
                  <p className="text-[11px] font-semibold text-yellow bg-yellow/10 border border-yellow/40 rounded-lg px-2.5 py-1.5">
                    ⚠ SLA D-10: o evento é em {diasAte <= 1 ? 'menos de 2 dias' : `${diasAte} dias`} — o ideal é solicitar este vídeo com 10+ dias de antecedência (roteiro + edição + 2 aprovações internas).
                  </p>
                );
              })()}
              <Field label="Clientes neste card">
                <div className="grid grid-cols-2 gap-1">
                  {solicitantes.filter((s) => s.status === 'Ativo' && s.tipo === 'Cliente externo').sort((a, b) => a.nome.localeCompare(b.nome)).map((s) => {
                    const checked = clientesTipoB.includes(s.id);
                    return (
                      <label key={s.id} className={`flex items-center gap-1.5 px-2 py-1 rounded border cursor-pointer text-[11px] transition-colors ${checked ? 'bg-purple/10 border-purple/30 text-text' : 'bg-panel-2 border-border text-text-2'}`}>
                        <input type="checkbox" checked={checked} onChange={() => {
                          setClientesTipoB((arr) => arr.includes(s.id) ? arr.filter((x) => x !== s.id) : [...arr, s.id]);
                        }} className="w-3 h-3 accent-purple" />
                        {s.nome}
                      </label>
                    );
                  })}
                </div>
                <p className="text-[10px] text-muted mt-1">{clientesTipoB.length} clientes selecionados</p>
              </Field>
            </>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Prioridade">
              <select className="input w-full text-sm" value={prioridade} onChange={(e) => setPrioridade(e.target.value as Prioridade)}>
                {PRIORIDADES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Prazo">
              <input type="date" className="input w-full text-sm" value={prazoSla} onChange={(e) => setPrazoSla(e.target.value)} />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Tipo de produto">
              <select className="input w-full text-sm" value={tipoProduto}
                onChange={(e) => { const tp = e.target.value as TipoProduto; setTipoProduto(tp); aplicarRoteamento(tp); }}>
                {(Object.keys(CHECKLIST_TEMPLATES) as TipoProduto[]).filter((t) => !TIPOS_APOSENTADOS.includes(t)).map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <p className="text-[10px] text-muted mt-1">Define o checklist e sugere as áreas (dono + apoio). Editável depois.</p>
            </Field>
            <Field label="Responsável principal (opcional)">
              <select className="input w-full text-sm" value={executorId} onChange={(e) => setExecutorId(e.target.value)}>
                <option value="">— deixar pra coordenação atribuir —</option>
                <optgroup label="Pode ser o próprio solicitante">
                  {pessoas.filter((p) => p.ativo).slice().sort((a, b) => a.nome.localeCompare(b.nome)).map((p) => (
                    <option key={p.id} value={p.id}>{p.nome} {p.papel ? `· ${p.papel}` : ''}</option>
                  ))}
                </optgroup>
              </select>
              <p className="text-[10px] text-muted mt-1">Cada item do checklist é atribuído depois, no detalhe da demanda.</p>
            </Field>
          </div>

          {TIPOS_COM_CANAL.includes(tipoProduto) && (
            <Field label="Canal · define o time e aciona os coordenadores">
              <select className="input w-full text-sm" value={canal}
                onChange={(e) => aplicarRoteamento(tipoProduto, e.target.value as Canal | '')}>
                <option value="">— definir canal —</option>
                <option value="digital">Digital</option>
                <option value="tv">TV</option>
              </select>
              <p className="text-[10px] text-muted mt-1">
                {canal ? `Aciona: ${COORDENADORES_CANAL[canal].join(' · ')}.` : 'Digital → Valentina/Agnes · TV → Fernanda Batista.'}
              </p>
            </Field>
          )}

          {tipoProduto === 'Matéria de TV' && (
            <Field label="Episódio (Mundo Business)">
              <select className="input w-full text-sm" value={episodioId} onChange={(e) => setEpisodioId(e.target.value)}>
                <option value="">— sem episódio —</option>
                {episodios
                  .filter((ep) => ep.titulo)
                  .slice()
                  .sort((a, b) => (a.temporada ?? '').localeCompare(b.temporada ?? '') || Number(a.numero ?? 0) - Number(b.numero ?? 0))
                  .map((ep) => (
                    <option key={ep.id} value={ep.id}>
                      {ep.temporada ? `${ep.temporada} · ` : ''}{ep.titulo}
                    </option>
                  ))}
              </select>
              <p className="text-[10px] text-muted mt-1">Vincula a matéria/chamada ao episódio do Mundo Business (só MB).</p>
            </Field>
          )}

          <Field label="Colaboradores (cards avulsos)">
            <details className="bg-panel-2 border border-border rounded">
              <summary className="px-2.5 py-1.5 cursor-pointer text-[11px] flex items-center justify-between">
                <span className="text-text-2 uppercase tracking-wide text-[9px] font-bold">Pessoas neste card</span>
                <span className="text-text-2 text-[10px] truncate ml-2 flex-1 text-right">
                  {colaboradores.length === 0
                    ? 'nenhum'
                    : pessoas.filter((p) => colaboradores.includes(p.id)).map((p) => p.nome.split(' ')[0]).join(', ')}
                </span>
              </summary>
              <div className="px-2.5 pb-2 pt-1 grid grid-cols-2 gap-1 max-h-40 overflow-y-auto">
                {pessoas.filter((p) => p.ativo).slice().sort((a, b) => a.nome.localeCompare(b.nome)).map((p) => {
                  const checked = colaboradores.includes(p.id);
                  return (
                    <label key={p.id} className="flex items-center gap-1.5 text-[11px] cursor-pointer">
                      <input type="checkbox" checked={checked}
                        onChange={() => setColaboradores((arr) => arr.includes(p.id) ? arr.filter((x) => x !== p.id) : [...arr, p.id])}
                        className="w-3 h-3 accent-blue-light" />
                      <span className={checked ? 'text-text' : 'text-text-2'}>{p.nome}</span>
                    </label>
                  );
                })}
              </div>
            </details>
            <p className="text-[10px] text-muted mt-1">
              Pessoas que participam deste card avulso (ad-hoc) — ex.: quem grava/edita. Cards gerados por contrato herdam os times pela matriz.
            </p>
          </Field>

          <Field label="Áreas executoras (gera 1 card por área no Kanban)">
            <div className="grid grid-cols-2 gap-1.5">
              {areas
                .filter((a) => !['Direção', 'Comercial'].includes(a.nome))
                .filter((a) => mostrarTodasAreas || areasIds.includes(a.id) || areasApoioIds.includes(a.id) || areaCasaTokens(a.nome, AREAS_POR_TIPO[tipoProduto]))
                .sort((a, b) => a.nome.localeCompare(b.nome)).map((a) => {
                const checked = areasIds.includes(a.id);
                return (
                  <label key={a.id} className={['flex items-center gap-1.5 px-2 py-1.5 rounded border cursor-pointer text-xs transition-colors', checked ? 'bg-blue/15 border-blue/40 text-blue-light' : 'bg-panel-2 border-border text-text-2 hover:border-border-light'].join(' ')}>
                    <input type="checkbox" checked={checked} onChange={() => { setAreasIds((arr) => arr.includes(a.id) ? arr.filter((x) => x !== a.id) : [...arr, a.id]); }} className="w-3 h-3 accent-blue-light" />
                    {a.nome}
                  </label>
                );
              })}
            </div>
            {AREAS_POR_TIPO[tipoProduto] && (
              <button
                type="button"
                className="mt-1.5 text-[11px] text-muted underline hover:text-text"
                onClick={() => setMostrarTodasAreas((v) => !v)}
              >
                {mostrarTodasAreas ? 'Mostrar só os times deste tipo de produto' : 'Mostrar todas as áreas'}
              </button>
            )}
            {areasApoioIds.length > 0 && (
              <div className="mt-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted">Apoio sugerido</span>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {areasApoioIds.map((id) => {
                    const a = areas.find((x) => x.id === id);
                    return (
                      <button key={id} type="button"
                        onClick={() => setAreasApoioIds((arr) => arr.filter((x) => x !== id))}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-panel-2 text-muted border border-border hover:border-red/40 hover:text-red"
                        title="Remover apoio">
                        {a?.nome ?? id} <X className="w-3 h-3" />
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-muted mt-1">Áreas de apoio (matriz v2). Não geram card próprio; clique para remover.</p>
              </div>
            )}
            {areasIds.length > 1 && (
              <label className="flex items-center gap-1.5 mt-2 text-[11px] text-text-2">
                <input type="checkbox" checked={splitByArea} onChange={(e) => setSplitByArea(e.target.checked)} className="w-3 h-3 accent-blue-light" />
                Criar card separado pra cada área ({areasIds.length} cards)
              </label>
            )}
          </Field>

          <Field label="Briefing">
            <textarea className="input w-full resize-none text-sm" rows={3} value={briefing} onChange={(e) => setBriefing(e.target.value)} placeholder="Contexto, referências, formato esperado..." />
          </Field>

          <Field label="Anexos (links Drive · Notion · WeTransfer · YouTube)">
            <div className="flex gap-1.5">
              <input className="input flex-1 text-sm" placeholder="cole o link e enter" value={novoAnexo} onChange={(e) => setNovoAnexo(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); adicionarAnexo(); } }} />
              <button type="button" onClick={adicionarAnexo} className="btn-secondary !px-2.5"><Plus className="w-3.5 h-3.5" /></button>
            </div>
            {anexos.length > 0 && (
              <div className="mt-2 space-y-1">
                {anexos.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 bg-panel-2 border border-border rounded px-2 py-1.5 text-xs">
                    <Paperclip className="w-3 h-3 text-muted shrink-0" />
                    <span className="flex-1 truncate text-text-2">{a}</span>
                    <button type="button" onClick={() => removerAnexo(i)} className="text-muted hover:text-red"><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[10px] text-muted mt-1.5">Links externos (Drive, YouTube, WeTransfer etc.)</p>
          </Field>

          {err && <div className="text-xs text-red bg-red/10 border border-red/30 rounded-lg px-2.5 py-1.5">{err}</div>}
        </div>
        <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-secondary !py-1.5 !px-3 text-xs">Cancelar</button>
          <button type="submit" disabled={create.isPending} className="btn-primary !py-1.5 !px-3 text-xs disabled:opacity-50">
            {create.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Criar demanda
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[10px] font-semibold text-text-2 uppercase tracking-wide mb-1">
        {label} {required && <span className="text-red">*</span>}
      </label>
      {children}
    </div>
  );
}
